import React from "react";
import { Box, Typography } from "@mui/material";

// Round the axis maximum up to a "nice" number (1/2/5 x 10^n)
const niceCeil = (value) => {
	if (!value || value <= 0) return 1;
	const pow10 = Math.pow(10, Math.floor(Math.log10(value)));
	const fraction = value / pow10;
	const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
	return nice * pow10;
};

/**
 * Dependency-free SVG bar chart.
 *
 * props:
 * - data: [{ label, [seriesKey]: number }]
 * - series: [{ key, label, color, colorByLabel: { [rowLabel]: color } }]
 * - height: chart height in px
 * - showValueLabels: draw the value above each bar
 * - showLegend: draw a legend row under the chart (use for >1 series)
 */
const SimpleBarChart = ({
	data = [],
	series = [],
	height = 360,
	showValueLabels = false,
	showLegend = false,
}) => {
	const viewWidth = 640;
	const viewHeight = 320;
	const margin = { top: 28, right: 16, bottom: 36, left: 52 };
	const plotWidth = viewWidth - margin.left - margin.right;
	const plotHeight = viewHeight - margin.top - margin.bottom;

	if (!data.length || !series.length) {
		return (
			<Box
				sx={{
					height,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: "#fff",
				}}
			>
				<Typography color="text.secondary">
					No data available for the selected period.
				</Typography>
			</Box>
		);
	}

	const maxValue = niceCeil(
		Math.max(
			1,
			...data.flatMap((row) =>
				series.map((s) => Number(row[s.key]) || 0)
			)
		)
	);
	const tickCount = 5;
	const ticks = Array.from(
		{ length: tickCount + 1 },
		(_, i) => (maxValue / tickCount) * i
	);

	const groupWidth = plotWidth / data.length;
	const barGap = 2; // surface gap between bars in a group
	const barWidth = Math.min(
		48,
		(groupWidth * 0.6 - barGap * (series.length - 1)) / series.length
	);

	// Grouped charts (e.g. 12 months x 2 series) leave very little room above
	// each bar - shrink the value labels so neighbouring ones don't collide.
	const valueFontSize = Math.max(7, Math.min(12, Math.round(barWidth * 0.7)));
	const valueLabelOffset = valueFontSize >= 12 ? 6 : 3;

	const yFor = (value) =>
		margin.top + plotHeight - (Number(value) / maxValue) * plotHeight;

	return (
		<Box sx={{ backgroundColor: "#fff", width: "100%" }}>
			<svg
				viewBox={`0 0 ${viewWidth} ${viewHeight}`}
				role="img"
				style={{ width: "100%", height: "auto", maxHeight: height, display: "block" }}
			>
				{/* horizontal gridlines + y labels */}
				{ticks.map((tick) => (
					<g key={tick}>
						<line
							x1={margin.left}
							x2={viewWidth - margin.right}
							y1={yFor(tick)}
							y2={yFor(tick)}
							stroke={tick === 0 ? "#9e9e9e" : "#e0e0e0"}
							strokeWidth="1"
						/>
						<text
							x={margin.left - 8}
							y={yFor(tick) + 4}
							textAnchor="end"
							fontSize="11"
							fill="#616161"
						>
							{Number(tick).toLocaleString()}
						</text>
					</g>
				))}

				{/* bars */}
				{data.map((row, rowIndex) => {
					const groupX = margin.left + rowIndex * groupWidth;
					const totalBarsWidth =
						series.length * barWidth + (series.length - 1) * barGap;
					const firstBarX = groupX + (groupWidth - totalBarsWidth) / 2;
					return (
						<g key={row.label}>
							{series.map((s, seriesIndex) => {
								const value = Number(row[s.key]) || 0;
								const barX =
									firstBarX +
									seriesIndex * (barWidth + barGap);
								const barY = yFor(value);
								const color =
									(s.colorByLabel &&
										s.colorByLabel[row.label]) ||
									s.color ||
									"#4472C4";
								return (
									<g key={s.key}>
										<rect
											x={barX}
											y={barY}
											width={barWidth}
											height={
												margin.top +
												plotHeight -
												barY
											}
											fill={color}
										>
											<title>
												{`${row.label}${
													series.length > 1
														? ` - ${s.label}`
														: ""
												}: ${value.toLocaleString()}`}
											</title>
										</rect>
										{showValueLabels && value > 0 && (
											<text
												x={barX + barWidth / 2}
												y={barY - valueLabelOffset}
												textAnchor="middle"
												fontSize={valueFontSize}
												fontWeight="bold"
												fill="#424242"
											>
												{value.toLocaleString()}
											</text>
										)}
									</g>
								);
							})}
							{/* x labels */}
							<text
								x={groupX + groupWidth / 2}
								y={margin.top + plotHeight + 18}
								textAnchor="middle"
								fontSize={data.length > 8 ? 10 : 11}
								fontWeight="bold"
								fill="#424242"
							>
								{row.label}
							</text>
						</g>
					);
				})}
			</svg>
			{showLegend && series.length > 1 && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						gap: 3,
						pb: 1,
					}}
				>
					{series.map((s) => (
						<Box
							key={s.key}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.75,
							}}
						>
							<Box
								sx={{
									width: 12,
									height: 12,
									backgroundColor: s.color,
								}}
							/>
							<Typography variant="body2">{s.label}</Typography>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

export default SimpleBarChart;
